import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { ResidencyAnswer } from '@/hooks/useResidency';
import OptionCard from './OptionCard';
import ResidencyShell from './ResidencyShell';

interface ResidencyQuestionProps {
  /** A previously stored answer, so returning to the screen keeps the choice. */
  initialAnswer: ResidencyAnswer | null;
  onSubmit: (answer: ResidencyAnswer) => void;
}

export default function ResidencyQuestion({
  initialAnswer,
  onSubmit,
}: ResidencyQuestionProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<ResidencyAnswer | null>(
    initialAnswer
  );

  const handleContinue = () => {
    if (selected) {
      onSubmit(selected);
    }
  };

  return (
    <ResidencyShell
      title={t('residency.title')}
      footer={
        <button
          type="button"
          disabled={!selected}
          onClick={handleContinue}
          className={`h-12 w-full max-w-[332px] rounded-[100px] text-base font-bold uppercase ${
            selected
              ? 'bg-[#569DE5] text-white'
              : 'cursor-not-allowed bg-[#eceef1] text-[#adb2ba]'
          }`}
        >
          {t('residency.continue')}
        </button>
      }
    >
      <div className="w-full max-w-[336px]">
        {/* Both paragraphs sit inside the described-by wrapper, so a screen
            reader announces the restriction and the ask, not just the first
            sentence. */}
        <div
          id="residency-description"
          className="text-center text-[15px] leading-[1.5] text-[#4a4e56]"
        >
          <p>{t('residency.description')}</p>
          <p className="mt-3">{t('residency.description_confirm')}</p>
        </div>

        <div
          role="radiogroup"
          aria-label={t('residency.title')}
          aria-describedby="residency-description"
          className="mt-[22px] flex flex-col gap-3"
        >
          <OptionCard
            testId="option-not-resident"
            selected={selected === 'other'}
            onSelect={() => setSelected('other')}
          >
            <Trans
              i18nKey="residency.option_not_resident"
              components={{ b: <b className="font-bold text-[#c0392b]" /> }}
            />
          </OptionCard>

          <OptionCard
            testId="option-resident"
            selected={selected === 'ca_il'}
            onSelect={() => setSelected('ca_il')}
          >
            {t('residency.option_resident')}
          </OptionCard>
        </div>
      </div>
    </ResidencyShell>
  );
}
